import { App, defineComponent, h } from 'vue'
// Importar el componente original de Vuetify
// Nota: "vuetify/components" expone VIcon
import { VIcon } from 'vuetify/components'
// Importar paths de @mdi/js (una vez) para resolver nombres mdi-*
import * as MDI from '@mdi/js'

// Utilidad: convertir 'mdi-office-building' -> 'mdiOfficeBuilding'
function mdiKebabToExport(name: string) {
  if (!name.startsWith('mdi-')) return name
  const rest = name.slice(4)
  const camel = rest.replace(/-([a-z0-9])/g, (_, c: string) => c.toUpperCase())
  return 'mdi' + camel.charAt(0).toUpperCase() + camel.slice(1)
}

// Heurística: detectar si una cadena parece path SVG
function looksLikeSvgPath(val: string) {
  return /^[MmZzLlHhVvCcSsQqTtAa][^a-z]*$/.test(val.trim())
}

/**
 * Plugin de compatibilidad para VIcon
 * - Si el consumidor usa <v-icon>mdi-...</v-icon> (texto en el slot por defecto),
 *   este plugin lo convierte automáticamente a prop `icon="mdi-..."`.
 * - Evita errores de SVG como: "<path> attribute d: Expected number, 'mdi-*'" cuando
 *   la app usa el set SVG (mdiSvg) y el slot de texto es interpretado como path.
 */
export default {
  install(app: App) {
    const Original = VIcon as any

    // Evitar doble registro si ya fue registrado (previene warning Vue warn: Component "VIcon" has already been registered)
    if ((app as any)._context?.components?.VIcon) {
      // Reemplazar sólo si aún no es nuestra versión compat (nombre distinto)
      const existing: any = (app as any)._context.components.VIcon
      if (existing && existing.name === 'VIconCompat') {
        return // ya instalado
      }
      // En lugar de retornar podríamos envolver, pero preferimos no duplicar
      // Log informativo único
      if (!(window as any).__VICON_COMPAT_ALREADY_LOGGED__) {
        console.info('[IconCompat] VIcon ya registrado; se mantiene instancia existente.')
        ;(window as any).__VICON_COMPAT_ALREADY_LOGGED__ = true
      }
      return
    }

    app.component(
      'VIcon',
      defineComponent({
  name: 'VIconCompat',
        inheritAttrs: false,
        // Reutilizar props del componente original para mantener la API
        props: (Original.props ?? {}) as any,
        setup(_props, { slots, attrs }) {
          const props = _props as any
          // Extraer el primer texto significativo del slot (ignorando espacios y fragmentos)
          function firstNonEmptyText(nodes: any[] | undefined): string {
            if (!nodes || nodes.length === 0) return ''
            const stack: any[] = [...nodes]
            while (stack.length) {
              const n = stack.shift()
              const c = (n && (n.children as any))
              if (typeof c === 'string') {
                const t = c.trim()
                if (t) return t
              } else if (Array.isArray(c)) {
                // Desapilar hijos (fragmentos)
                stack.unshift(...c)
              }
            }
            return ''
          }
          return () => {
            // Extraer icono del prop o del slot
            const slotText = firstNonEmptyText(slots.default?.())
            const incoming = props?.icon ?? slotText

            // Resolver nombres mdi-* a path SVG de @mdi/js
            if (typeof incoming === 'string' && !looksLikeSvgPath(incoming)) {
              // Alias de Vuetify (ej: $close) -> dejar al parser de Vuetify
              if (incoming.startsWith('$')) {
                return h(Original, { ...props, ...attrs, icon: incoming })
              }
              // Soportar 'mdi:<name>' resolviendo a path SVG
              const mdiSetMatch = incoming.match(/^mdi:([a-z0-9-]+)$/i)
              if (mdiSetMatch) {
                const name = mdiSetMatch[1]
                const exportName = mdiKebabToExport('mdi-' + name)
                const path = (MDI as any)[exportName]
                return h(Original, { ...props, ...attrs, icon: typeof path === 'string' ? path : '' })
              }

              if (incoming.startsWith('mdi-')) {
                const exportName = mdiKebabToExport(incoming)
                const path = (MDI as any)[exportName]
                return h(Original, { ...props, ...attrs, icon: typeof path === 'string' ? path : '' })
              }
            }

            // Si no hay prop icon y el slot es un mdi-... plano, mapearlo al prop
      if (!props?.icon && slotText) {
              if (/^mdi-[a-z0-9-]+$/i.test(slotText)) {
                const exportName = mdiKebabToExport(slotText)
                const path = (MDI as any)[exportName]
        return h(Original, { ...props, ...attrs, icon: typeof path === 'string' ? path : '' })
              }
            }

            // Fallback: render original con props/slots tal cual
            return h(Original, { ...props, ...attrs }, slots)
          }
        },
      })
    )
  },
}
